import React, { useCallback, useMemo, useState } from 'react';
import HomePage from './pages/HomePage';
import NewsfeedPage from './pages/NewsfeedPage';
import AskBragaPage from './pages/AskBragaPage';
import GmmDashboardPage from './pages/GmmDashboardPage';
import { IntelContext } from './IntelContext';
import gmmPfaudlerLogo from '../images/logos/gmm.jpeg';
import './GmmIntel.css';

const INTEL_LINKS = [
  { tab: 'home', label: 'Home' },
  { tab: 'newsfeed', label: 'Newsfeed' },
  { tab: 'braga', label: 'Ask Braga' },
  { tab: 'dashboard', label: 'Dashboard' },
];

const GmmIntel = () => {
  const [activeTab, setActiveTab] = useState('home');
  const [toast, setToast] = useState(null);
  const [intelBridge, setIntelBridge] = useState({ nonce: 0 });

  const [shell, setShell] = useState({
    companiesOpen: false,
    accountOpen: false,
    menuOpen: false,
    settingsOpen: false,
  });

  const showToast = useCallback((message) => {
    const id = Date.now();
    setToast({ id, message });
    window.setTimeout(() => {
      setToast((t) => (t && t.id === id ? null : t));
    }, 2600);
  }, []);

  const navigate = useCallback((tab, options = {}) => {
    setActiveTab(tab);
    setIntelBridge((prev) => ({
      tab,
      nonce: prev.nonce + 1,
      ...options,
    }));
  }, []);

  const closeMenus = () => {
    setShell((s) => ({
      ...s,
      companiesOpen: false,
      accountOpen: false,
    }));
  };

  const getPageTitle = () => {
    switch (activeTab) {
      case 'home':
        return 'Homepage';
      case 'newsfeed':
        return 'Newsfeed';
      case 'braga':
        return 'Ask Braga';
      case 'dashboard':
        return 'Competitive Edge Dashboard';
      default:
        return 'Homepage';
    }
  };

  const renderPage = () => {
    switch (activeTab) {
      case 'home':
        return <HomePage />;
      case 'newsfeed':
        return <NewsfeedPage intelBridge={intelBridge} />;
      case 'braga':
        return <AskBragaPage intelBridge={intelBridge} />;
      case 'dashboard':
        return <GmmDashboardPage intelBridge={intelBridge} />;
      default:
        return <HomePage />;
    }
  };

  const contentClass = [
    'page-content',
    activeTab === 'dashboard' ? 'page-content--dashboard' : '',
    activeTab === 'braga' ? 'page-content--braga' : '',
    activeTab === 'newsfeed' ? 'page-content--newsfeed' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const ctx = useMemo(
    () => ({
      toast: showToast,
      navigate,
      intelBridge,
      activeTab,
    }),
    [showToast, navigate, intelBridge, activeTab],
  );

  return (
    <IntelContext.Provider value={ctx}>
      <div className="gmm-intel">
        {toast ? (
          <div className="gmm-intel-toast" role="status">
            {toast.message}
          </div>
        ) : null}

        <nav className="gmm-navbar">
          <div className="navbar-left">
            <button
              type="button"
              className="gmm-navbar-brand gmm-plain-btn"
              onClick={() => {
                closeMenus();
                navigate('home');
              }}
              aria-label="GMM Intel — Home"
            >
              <img
                src={gmmPfaudlerLogo}
                alt=""
                className="gmm-navbar-brand-logo"
                width={28}
                height={28}
              />
              <span className="gmm-navbar-brand-text">GMM Pfaudler</span>
            </button>

            <button
              type="button"
              className={`nav-tab ${activeTab === 'home' ? 'active' : ''}`}
              onClick={() => {
                closeMenus();
                navigate('home');
              }}
            >
              Home
            </button>

            <button
              type="button"
              className={`nav-tab ${activeTab === 'newsfeed' ? 'active' : ''}`}
              onClick={() => {
                closeMenus();
                navigate('newsfeed');
              }}
            >
              Newsfeed
            </button>

            <button
              type="button"
              className={`nav-tab ${activeTab === 'braga' ? 'active' : ''}`}
              onClick={() => {
                closeMenus();
                navigate('braga');
              }}
            >
              Braga
              <span className="dropdown-indicator">▼</span>
            </button>

            <button
              type="button"
              className={`nav-tab ${activeTab === 'dashboard' ? 'active' : ''}`}
              onClick={() => {
                closeMenus();
                navigate('dashboard');
              }}
            >
              Dashboard
            </button>
          </div>

          <div className="navbar-right">
            <div className="gmm-navbar-dropdown-wrap">
              <button
                type="button"
                className="nav-btn"
                onClick={() => {
                  closeMenus();
                  navigate('braga');
                }}
              >
                Ask Braga
              </button>
            </div>

            <div className="gmm-navbar-dropdown-wrap">
              <button
                type="button"
                className="nav-btn companies-btn"
                onClick={() =>
                  setShell((s) => ({
                    ...s,
                    companiesOpen: !s.companiesOpen,
                    accountOpen: false,
                  }))
                }
              >
                Companies
                <span className="dropdown-indicator">▼</span>
              </button>
              {shell.companiesOpen ? (
                <div className="gmm-dropdown-panel companies-panel" role="menu">
                  {['Competitors', 'Customers', 'Suppliers', 'Partners'].map((label) => (
                    <button
                      key={label}
                      type="button"
                      className="gmm-dropdown-item"
                      role="menuitem"
                      onClick={() => {
                        showToast(`${label} — detail view opens from Newsfeed filters (demo)`);
                        setShell((s) => ({ ...s, companiesOpen: false }));
                        navigate('newsfeed');
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>

            <button
              type="button"
              className="nav-btn icon-btn"
              aria-label="Settings"
              onClick={() =>
                setShell((s) => ({ ...s, settingsOpen: true, companiesOpen: false }))
              }
            >
              ···
            </button>

            <div className="gmm-navbar-dropdown-wrap">
              <button
                type="button"
                className="nav-btn icon-btn user-btn"
                aria-label="Account"
                onClick={() =>
                  setShell((s) => ({
                    ...s,
                    accountOpen: !s.accountOpen,
                    companiesOpen: false,
                  }))
                }
              >
                ●
              </button>
              {shell.accountOpen ? (
                <div className="gmm-dropdown-panel account-panel">
                  <div className="gmm-account-line">John Analyst · GMM Pfaudler India</div>
                  <button
                    type="button"
                    className="gmm-dropdown-item danger"
                    onClick={() => {
                      showToast('Signed out (demo)');
                      setShell((s) => ({ ...s, accountOpen: false }));
                    }}
                  >
                    Sign Out
                  </button>
                </div>
              ) : null}
            </div>

            <button
              type="button"
              className="nav-btn icon-btn"
              aria-label="Menu"
              onClick={() =>
                setShell((s) => ({
                  ...s,
                  menuOpen: true,
                  companiesOpen: false,
                  accountOpen: false,
                }))
              }
            >
              ≡
            </button>
          </div>
        </nav>

        {shell.settingsOpen ? (
          <div
            className="gmm-modal-backdrop"
            role="presentation"
            onClick={() => setShell((s) => ({ ...s, settingsOpen: false }))}
          >
            <div
              className="gmm-modal"
              role="dialog"
              aria-labelledby="settings-soon-title"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 id="settings-soon-title" className="gmm-modal-title">
                Settings — Coming Soon
              </h2>
              <button
                type="button"
                className="gmm-btn-primary"
                onClick={() => setShell((s) => ({ ...s, settingsOpen: false }))}
              >
                Close
              </button>
            </div>
          </div>
        ) : null}

        <div className={`gmm-drawer-backdrop ${shell.menuOpen ? 'open' : ''}`}>
          <div
            role="presentation"
            className="gmm-drawer-scrim"
            onClick={() => setShell((s) => ({ ...s, menuOpen: false }))}
          />
          <aside className={`gmm-drawer ${shell.menuOpen ? 'open' : ''}`}>
            <div className="gmm-drawer-header">
              <span>Navigate</span>
              <button
                type="button"
                className="gmm-drawer-close"
                aria-label="Close menu"
                onClick={() => setShell((s) => ({ ...s, menuOpen: false }))}
              >
                ×
              </button>
            </div>
            <nav className="gmm-drawer-nav">
              {INTEL_LINKS.map((l) => (
                <button
                  key={l.tab}
                  type="button"
                  className={`gmm-drawer-link ${activeTab === l.tab ? 'active' : ''}`}
                  onClick={() => {
                    navigate(l.tab);
                    setShell((s) => ({ ...s, menuOpen: false }));
                  }}
                >
                  {l.label}
                </button>
              ))}
            </nav>
          </aside>
        </div>

        <div className={contentClass}>
          <header className="gmm-content-heading">
            <h1 className="gmm-content-title">{getPageTitle()}</h1>
          </header>
          {renderPage()}
        </div>
      </div>
    </IntelContext.Provider>
  );
};

export default GmmIntel;
